import { SHORTCUTS } from '../../lib/shortcuts.js';
import type { XRStatus } from '../../xr/types.js';
import type { ComponentInstance } from '../component.js';
import { setHtmlIcon } from '../dom-helpers.js';
import {
  ICON_CAMERA,
  ICON_FULLSCREEN,
  ICON_MEASURE,
  ICON_PERF,
  ICON_RESET,
  ICON_SHARE,
  ICON_WALK,
  ICON_XR,
} from '../icons.js';

export interface ToolbarProps {
  embed: boolean;
  // 4.M: when true, the walk button is aria-disabled (still clickable
  // for an educational toast). The shortcut handler in the orchestrator
  // applies the same gating independently.
  pointerCoarse: boolean;
  // Phase 3 M3 (Codex iter 4 NEW2 + iter 5 NEW1): screenshot button must
  // be disabled when XR session is starting/active. SparkViewerAdapter
  // captureFrame rejects in those states; without UI gating the user sees
  // no feedback and the reject is swallowed by the orchestrator caller.
  xrStatus: XRStatus;
}

export interface ToolbarCallbacks {
  onScreenshot: () => void;
  onReset: () => void;
  onFullscreenToggle: () => void;
  onPerfToggle: () => void;
  onShare: () => void;
  onOpenXR: () => void;
  onWalkToggle: () => void;
  // Step 7 #2 fix: explicit toolbar entry for measurement mode (the
  // 'd' shortcut alone was undiscoverable).
  onMeasureToggle: () => void;
  // Called when the user clicks a feature that's available in the
  // current capability set (e.g. walk on touch). The toolbar swallows
  // the gesture and forwards a reason so the renderer can show a toast.
  onUnavailableFeature: (reason: 'walk-touch') => void;
}

interface ButtonSpec {
  key: string;
  icon: string;
  label: string;
  // Step 7 #3 fix: shortcut is optional. Buttons without shortcut don't
  // expose aria-keyshortcuts and the title shows just the label, so the
  // user can't trigger them by accidentally pressing a key while moving
  // in walk mode (WASD vs S=screenshot collision).
  shortcut?: string;
  onClick: () => void;
}

function buildButton(spec: ButtonSpec): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.classList.add('viewer-ui-interactive');
  b.classList.add('viewer-ui-tap-target');
  if (spec.shortcut !== undefined) {
    b.dataset['shortcut'] = spec.shortcut;
    b.setAttribute('aria-keyshortcuts', spec.shortcut.toUpperCase());
  }
  b.setAttribute('aria-label', spec.label);
  b.title =
    spec.shortcut !== undefined ? `${spec.label} (${spec.shortcut.toUpperCase()})` : spec.label;
  b.style.cssText =
    'display:flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;background:rgba(15,15,17,0.7);backdrop-filter:blur(8px);color:#f4f4f5;border:1px solid rgba(255,255,255,0.08);border-radius:0.375rem;cursor:pointer;transition:background 100ms';
  b.addEventListener('mouseenter', () => {
    if (b.getAttribute('aria-disabled') === 'true') return;
    b.style.background = 'rgba(30,30,35,0.85)';
  });
  b.addEventListener('mouseleave', () => {
    b.style.background = 'rgba(15,15,17,0.7)';
  });
  setHtmlIcon(b, spec.icon);
  b.addEventListener('click', spec.onClick);
  return b;
}

function setWalkDisabled(walkBtn: HTMLButtonElement, disabled: boolean): void {
  walkBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  walkBtn.style.opacity = disabled ? '0.5' : '1';
  walkBtn.style.cursor = disabled ? 'not-allowed' : 'pointer';
  if (disabled) {
    walkBtn.title = 'Walk Mode richiede mouse + tastiera';
  } else {
    walkBtn.title = `Modalità walk (V)`;
  }
}

function setScreenshotDisabled(btn: HTMLButtonElement, disabled: boolean): void {
  btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  btn.style.opacity = disabled ? '0.5' : '1';
  btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
  btn.title = disabled
    ? 'Cattura schermata non disponibile durante la sessione XR'
    : 'Cattura schermata';
}

function isXrCaptureBlocked(status: XRStatus): boolean {
  return status === 'starting' || status === 'in-session';
}

export function mountToolbar(
  root: HTMLElement,
  initial: ToolbarProps,
  callbacks: ToolbarCallbacks,
): ComponentInstance<ToolbarProps> {
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'toolbar');
  wrap.setAttribute('aria-label', 'Controlli viewer');
  wrap.setAttribute('aria-orientation', 'horizontal');
  wrap.style.cssText = initial.embed
    ? 'position:fixed;top:0.5rem;right:0.5rem;display:flex;gap:0.25rem;z-index:11;pointer-events:auto'
    : 'position:fixed;top:1rem;right:1rem;display:flex;gap:0.5rem;z-index:11;pointer-events:auto';

  wrap.appendChild(
    buildButton({
      key: 'fullscreen',
      icon: ICON_FULLSCREEN,
      label: 'Schermo intero',
      shortcut: SHORTCUTS.fullscreen,
      onClick: callbacks.onFullscreenToggle,
    }),
  );
  const screenshotBtn = buildButton({
    key: 'screenshot',
    icon: ICON_CAMERA,
    label: 'Cattura schermata',
    // Step 7 #3 fix: no shortcut — collision with WASD walk movement.
    onClick: () => {
      if (screenshotBtn.getAttribute('aria-disabled') === 'true') return;
      callbacks.onScreenshot();
    },
  });
  wrap.appendChild(screenshotBtn);
  setScreenshotDisabled(screenshotBtn, isXrCaptureBlocked(initial.xrStatus));
  wrap.appendChild(
    buildButton({
      key: 'reset',
      icon: ICON_RESET,
      label: 'Reset vista',
      shortcut: SHORTCUTS.reset,
      onClick: callbacks.onReset,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'measure',
      icon: ICON_MEASURE,
      label: 'Misura distanze (clicca due punti)',
      // Step 7 #3 fix: no shortcut — D collides with WASD right-strafe in walk mode.
      onClick: callbacks.onMeasureToggle,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'perf',
      icon: ICON_PERF,
      label: 'Statistiche performance',
      shortcut: SHORTCUTS.perf,
      onClick: callbacks.onPerfToggle,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'share',
      icon: ICON_SHARE,
      label: 'Condividi vista',
      shortcut: SHORTCUTS.share,
      onClick: callbacks.onShare,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'xr',
      icon: ICON_XR,
      label: 'Apri in VR / AR',
      shortcut: SHORTCUTS.xr,
      onClick: callbacks.onOpenXR,
    }),
  );
  // 4.M: walk button. On touch devices the click is intercepted and
  // forwarded to onUnavailableFeature instead of onWalkToggle so the
  // renderer can show an educational toast.
  const walkBtn = buildButton({
    key: 'walk',
    icon: ICON_WALK,
    label: 'Modalità walk',
    shortcut: SHORTCUTS.walk,
    onClick: () => {
      if (walkBtn.getAttribute('aria-disabled') === 'true') {
        callbacks.onUnavailableFeature('walk-touch');
        return;
      }
      callbacks.onWalkToggle();
    },
  });
  wrap.appendChild(walkBtn);
  setWalkDisabled(walkBtn, initial.pointerCoarse);

  root.appendChild(wrap);

  return {
    update(props: ToolbarProps) {
      // Reconcile walk-disabled state on live device-state changes
      // (Surface 2-in-1 keyboard plug/unplug).
      setWalkDisabled(walkBtn, props.pointerCoarse);
      setScreenshotDisabled(screenshotBtn, isXrCaptureBlocked(props.xrStatus));
    },
    cleanup() {
      wrap.remove();
    },
  };
}
